import { supabase } from "@/lib/supabase";

// ============================================================
// Automatische Bild-Aktualisierung
// ------------------------------------------------------------
// Der CDN von Supabase liefert ein Bild unter derselben Adresse weiter,
// auch wenn die Datei im Speicher ausgetauscht wurde. Deshalb hängt die
// Website an jede Bild-Adresse ein Versions-Häkchen (?v=…).
//
// Diese Version wird jetzt AUTOMATISCH bestimmt:
//   • Beim Laden fragt die Seite im Hintergrund bei der Supabase-Funktion
//     "image-cache-version" nach dem jüngsten Änderungszeitpunkt der Bilder.
//   • Hat sich seit dem letzten Besuch etwas geändert, wird die Seite
//     einmal automatisch neu geladen – so erscheint das neue Bild sofort.
//   • Ohne Internet / bei Fehlern wird einfach die zuletzt bekannte
//     Version verwendet (die Seite bleibt normal nutzbar).
//
// Ein Bild austauschen = neue Datei mit demselben Namen hochladen. Fertig.
// ============================================================

const VERSION_KEY = "site-images-version";
const SESSION_KEY = "site-images-version-reloaded";

// Startwert, falls im Browser noch keine Version bekannt ist.
const FALLBACK_VERSION = "2026-10-11";

/** Liefert die aktuell bekannte Bild-Version (synchron, für img()). */
export function getImageVersion(): string {
  if (typeof window === "undefined") return FALLBACK_VERSION;
  try {
    return window.localStorage.getItem(VERSION_KEY) || FALLBACK_VERSION;
  } catch {
    return FALLBACK_VERSION;
  }
}

/**
 * Prüft im Hintergrund, ob sich Bilder geändert haben. Wurde eine neue
 * Version erkannt, wird die Seite einmalig automatisch neu geladen.
 * Wird beim App-Start aufgerufen und nie abgewartet.
 */
export async function ensureFreshImages(): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("image-cache-version");
    if (error) return;

    const version = (data as { version?: string } | null)?.version;
    if (!version) return;

    const cached = window.localStorage.getItem(VERSION_KEY);
    window.localStorage.setItem(VERSION_KEY, version);

    // Nur neu laden, wenn bereits eine (andere) Version bekannt war und
    // in dieser Sitzung noch nicht für diese Version neu geladen wurde.
    if (cached && cached !== version) {
      if (window.sessionStorage.getItem(SESSION_KEY) !== version) {
        window.sessionStorage.setItem(SESSION_KEY, version);
        window.location.reload();
      }
    }
  } catch {
    // Offline oder Fehler – Seite läuft normal weiter.
  }
}